import React, { useState } from "react";
import { Link } from "react-router-dom";

import { appNav } from "../../routes/appNavigation";
import type { Company } from "../../types";
import "./BusinessInfoPanel.css";

type Props = {
  company?: Company | null;
  loading?: boolean;
};

type Field = { label: string; value?: string | null };

function FieldList({ fields }: { fields: Field[] }) {
  const filled = fields.filter((field) => field.value?.trim());
  if (filled.length === 0) {
    return <p className="eventos-subtle">Not set yet.</p>;
  }
  return (
    <dl className="business-info__fields">
      {filled.map((field) => (
        <div key={field.label} className="business-info__field">
          <dt>{field.label}</dt>
          <dd>{field.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * "Your Business Information" — invoice info and bank details as they will
 * appear on the PDF.
 *
 * Collapsed by default per the sprint: it is reference data the user rarely
 * changes, and it was pushing the actual form below the fold. Read-only here
 * because these fields belong to the company and are edited in Profile &
 * Settings (Sprint 11 moved them out of the per-event invoice form).
 */
export const BusinessInfoPanel: React.FC<Props> = ({ company, loading }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className={`business-info${expanded ? " is-expanded" : ""}`}>
      <button
        type="button"
        className="business-info__head"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
        aria-controls="business-info-body"
      >
        <span className="eventos-label">Your business information</span>
        <span className="eventos-subtle business-info__summary">
          {loading ? "Loading…" : company?.name || "Not set"}
        </span>
        <span className="business-info__chevron" aria-hidden>
          {expanded ? "▾" : "▸"}
        </span>
      </button>

      <div id="business-info-body" hidden={!expanded} className="business-info__body">
        <p className="business-info__hint">
          Appears as the seller on every invoice PDF — this is your company, not Zoniq. Edit in{" "}
          <Link to={appNav.profile()}>Profile &amp; Settings</Link>.
        </p>

        <div className="business-info__cols">
          <div>
            <h3 className="business-info__subtitle">Invoice information</h3>
            <FieldList
              fields={[
                { label: "Business name", value: company?.name },
                { label: "GSTIN", value: company?.invoice_gstin },
                { label: "Address", value: company?.invoice_address },
                { label: "State", value: company?.invoice_state_name },
                { label: "State code", value: company?.invoice_state_code },
              ]}
            />
          </div>
          <div>
            <h3 className="business-info__subtitle">Bank details</h3>
            <FieldList
              fields={[
                { label: "Account holder", value: company?.bank_account_holder },
                { label: "Account number", value: company?.bank_account_number },
                { label: "IFSC", value: company?.bank_ifsc_code },
                { label: "Branch", value: company?.bank_branch },
              ]}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessInfoPanel;
